import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getProjectBatches,
  getProjectBatch,
  getProjectBatchesStats,
  getProjectLatestBatch,
  createProjectBatch,
  deleteProjectBatch,
  getProjectBatchCalculations,
  generateAutoReport,
  uploadManualReport,
  approveBatchAll,
  rejectBatchAll,
  approvePreDistribution,
  rejectPreDistribution,
  markPreDistributionReady,
} from "../api/batchesApi";
import { showAlert } from "../slices/alertsSlice";
import {
  setBatchesList,
  setCurrentBatch,
  setLatestBatch,
  setBatchesStats,
  setBatchCalculations,
} from "../slices/batchesSlice";
import { handleError } from "../slices/errorsSlice";
import { startLoading, endLoading } from "../slices/loadingSlice";

// GET All Batches for Project
export const getProjectBatchesList = createAsyncThunk(
  "batches/getProjectBatchesList",
  ({ projectId, query = {} }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    getProjectBatches(token, projectId, query)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) =>
        dispatch(
          setBatchesList({
            batches: data.batches,
            pagination: data.pagination,
          })
        )
      )
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

// GET One Batch
export const getCurrentProjectBatch = createAsyncThunk(
  "batches/getCurrentProjectBatch",
  ({ projectId, batchId }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    getProjectBatch(token, projectId, batchId)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => dispatch(setCurrentBatch({ batch: data.batch })))
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

// GET Batches Summary/Stats
export const getProjectBatchesStatsSummary = createAsyncThunk(
  "batches/getProjectBatchesStatsSummary",
  ({ projectId }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    getProjectBatchesStats(token, projectId)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => dispatch(setBatchesStats({ summary: data.summary })))
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

// GET Latest Batch
export const getLatestProjectBatch = createAsyncThunk(
  "batches/getLatestProjectBatch",
  ({ projectId }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    getProjectLatestBatch(token, projectId)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => dispatch(setLatestBatch({ batch: data.batch })))
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

// POST Create Batch
export const createNewProjectBatch = createAsyncThunk(
  "batches/createNewProjectBatch",
  ({ projectId, force = false, callback, onConflict }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    createProjectBatch(token, projectId, { force })
      .then((response) => {
        if (response.status === 201) {
          return response.json();
        }
        if (response.status === 409 && onConflict) {
          onConflict();
          return null;
        }
        return Promise.reject(response);
      })
      .then((data) => {
        if (data) {
          dispatch(setCurrentBatch({ batch: data.batch }));
          dispatch(showAlert({ message: "Batch generado exitosamente", type: "success" }));
          if (callback) callback(data.batch);
        }
      })
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

// DELETE Batch
export const deleteCurrentProjectBatch = createAsyncThunk(
  "batches/deleteCurrentProjectBatch",
  ({ projectId, batchId, callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    deleteProjectBatch(token, projectId, batchId)
      .then((response) =>
        response.status === 200 ? true : Promise.reject(response)
      )
      .then(() => {
        dispatch(showAlert({ message: "Batch eliminado", type: "success" }));
        if (callback) callback();
      })
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

// GET Batch Calculations (Pre-distributions)
export const getBatchCalculationsList = createAsyncThunk(
  "batches/getBatchCalculationsList",
  ({ projectId, batchId, query = {} }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    getProjectBatchCalculations(token, projectId, batchId, query)
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) =>
        dispatch(
          setBatchCalculations({
            calculations: data.calculations,
            pagination: data.pagination,
          })
        )
      )
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

// POST Generate Auto Report
export const createAutoReport = createAsyncThunk(
  "batches/createAutoReport",
  ({ preDistributionId, projectId, batchId, query, callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    generateAutoReport(token, preDistributionId)
      .then((response) =>
        response.status === 201 ? response.json() : Promise.reject(response)
      )
      .then(() => {
        dispatch(
          showAlert({
            message: "Reporte automático generado exitosamente",
            type: "success",
          })
        );
        // Refrescar la lista de cálculos para obtener los reportes actualizados
        return getProjectBatchCalculations(token, projectId, batchId, query);
      })
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => {
        dispatch(
          setBatchCalculations({
            calculations: data.calculations,
            pagination: data.pagination,
          })
        );
        if (callback) callback();
      })
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

// POST Upload Manual Report
export const uploadPreDistributionManualReport = createAsyncThunk(
  "batches/uploadPreDistributionManualReport",
  ({ preDistributionId, projectId, batchId, query, file, notes, callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    uploadManualReport(token, preDistributionId, file, notes)
      .then((response) =>
        response.status === 201 ? response.json() : Promise.reject(response)
      )
      .then((data) => {
        dispatch(
          showAlert({
            message: data.message || "Reporte manual subido exitosamente",
            type: "success",
          })
        );
        // Refrescar la lista de cálculos para obtener los reportes actualizados
        return getProjectBatchCalculations(token, projectId, batchId, query);
      })
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => {
        dispatch(
          setBatchCalculations({
            calculations: data.calculations,
            pagination: data.pagination,
          })
        );
        if (callback) callback();
      })
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

// POST Approve Batch (approve-all)
export const approveProjectBatchAll = createAsyncThunk(
  "batches/approveProjectBatchAll",
  ({ projectId, batchId, notes, callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    approveBatchAll(token, projectId, batchId, { notes })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        }
        return Promise.reject(response);
      })
      .then((data) => {
        const isFullyApproved = data.data?.status === "approved";
        const message = isFullyApproved
          ? `Batch aprobado exitosamente (${data.data.approvalPercentage}% aprobadas)`
          : `Batch parcialmente aprobado (${data.data.approvalPercentage}% aprobadas, se requiere 90% para aprobación completa)`;

        dispatch(showAlert({ message, type: "success" }));

        // Actualizar el batch actual si está cargado
        if (data.data) {
          dispatch(setCurrentBatch({ batch: { ...data.data, _id: batchId } }));
        }

        if (callback) callback(data.data);
      })
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

// POST Reject Batch (reject-all)
export const rejectProjectBatchAll = createAsyncThunk(
  "batches/rejectProjectBatchAll",
  ({ projectId, batchId, reason, skipValidation = false, callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    rejectBatchAll(token, projectId, batchId, { reason, skipValidation })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        }
        return Promise.reject(response);
      })
      .then((data) => {
        dispatch(
          showAlert({
            message: data.message || "Batch rechazado exitosamente",
            type: "success",
          })
        );

        // Actualizar el batch actual si está cargado
        if (data.data) {
          dispatch(setCurrentBatch({ batch: { ...data.data, _id: batchId } }));
        }

        if (callback) callback(data.data);
      })
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

// POST Approve Pre-Distribution Individual
export const approveProjectPreDistribution = createAsyncThunk(
  "batches/approveProjectPreDistribution",
  ({ preDistributionId, projectId, batchId, query, notes, skipValidation = false, callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    approvePreDistribution(token, preDistributionId, { notes, skipValidation })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        }
        return Promise.reject(response);
      })
      .then((data) => {
        dispatch(
          showAlert({
            message: data.message || "Pre-distribution aprobada exitosamente",
            type: "success",
          })
        );
        // Refrescar la lista de cálculos para obtener el estado actualizado
        return getProjectBatchCalculations(token, projectId, batchId, query);
      })
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => {
        dispatch(
          setBatchCalculations({
            calculations: data.calculations,
            pagination: data.pagination,
          })
        );
        // También refrescar el batch para actualizar su status
        return getProjectBatch(token, projectId, batchId);
      })
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => {
        dispatch(setCurrentBatch({ batch: data.batch }));
        if (callback) callback();
      })
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

// POST Reject Pre-Distribution Individual
export const rejectProjectPreDistribution = createAsyncThunk(
  "batches/rejectProjectPreDistribution",
  ({ preDistributionId, projectId, batchId, query, reason, skipValidation = false, callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    rejectPreDistribution(token, preDistributionId, { reason, skipValidation })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        }
        return Promise.reject(response);
      })
      .then((data) => {
        dispatch(
          showAlert({
            message: data.message || "Pre-distribution rechazada exitosamente",
            type: "success",
          })
        );
        // Refrescar la lista de cálculos para obtener el estado actualizado
        return getProjectBatchCalculations(token, projectId, batchId, query);
      })
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => {
        dispatch(
          setBatchCalculations({
            calculations: data.calculations,
            pagination: data.pagination,
          })
        );
        // También refrescar el batch para actualizar su status
        return getProjectBatch(token, projectId, batchId);
      })
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => {
        dispatch(setCurrentBatch({ batch: data.batch }));
        if (callback) callback();
      })
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);

// POST Mark Pre-Distribution as Ready for Approval
export const markProjectPreDistributionReady = createAsyncThunk(
  "batches/markProjectPreDistributionReady",
  ({ preDistributionId, projectId, batchId, query, callback }, { getState, dispatch }) => {
    const { token } = getState().auth;

    dispatch(startLoading());

    markPreDistributionReady(token, preDistributionId)
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        }
        return Promise.reject(response);
      })
      .then((data) => {
        dispatch(
          showAlert({
            message: data.message || "Pre-distribution marcada como lista para aprobación",
            type: "success",
          })
        );
        // Refrescar la lista de cálculos para obtener el estado actualizado
        return getProjectBatchCalculations(token, projectId, batchId, query);
      })
      .then((response) =>
        response.status === 200 ? response.json() : Promise.reject(response)
      )
      .then((data) => {
        dispatch(
          setBatchCalculations({
            calculations: data.calculations,
            pagination: data.pagination,
          })
        );
        if (callback) callback();
      })
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(endLoading()));
  }
);
